import { isHttpClientError } from "../../../lib/http/errors";
import type { AuthzChangeSummaryInput, AuthzTreeNode } from "../types";

const ROLE_CODE_PATTERN = /^[a-z][a-z0-9_]*$/;

/** 校验新增角色表单并返回可直接展示的错误提示。 */
export function validateAuthzRoleCreateInput(input: {
  code: string;
  name: string;
  description: string;
}): string | null {
  const code = input.code.trim();
  const name = input.name.trim();
  const description = input.description.trim();
  if (!code || !name) {
    return "请填写角色编码和名称。";
  }
  if (code.length < 2 || code.length > 64) {
    return "角色编码长度应为 2 至 64 个字符。";
  }
  if (!ROLE_CODE_PATTERN.test(code)) {
    return "角色编码格式不正确：请以小写字母开头，仅使用小写字母、数字或下划线。";
  }
  if (name.length > 128) {
    return "角色名称不能超过 128 个字符。";
  }
  if (description.length > 1000) {
    return "角色说明不能超过 1000 个字符。";
  }
  return null;
}

/** 根据统一 HTTP 错误返回新增角色失败提示。 */
export function getAuthzRoleCreateErrorMessage(error: unknown): string {
  if (!isHttpClientError(error)) {
    return "角色创建失败，请稍后重试。";
  }
  switch (error.statusCode) {
    case 401:
      return "登录状态已失效，请重新登录。";
    case 403:
      return "当前账号没有新增角色权限。";
    case 409:
      return "角色编码或名称已存在，请更换后重试。";
    case 422:
      return "角色信息格式不正确，请检查编码、名称和说明后重试。";
    case 429:
      return "操作过于频繁，请稍后重试。";
    default:
      return error.statusCode !== null && error.statusCode >= 500
        ? "角色服务暂时不可用，请稍后重试。"
        : "角色创建失败，请稍后重试。";
  }
}

/** 根据统一 HTTP 错误返回角色配置保存失败提示。 */
export function getAuthzRoleConfigurationSaveErrorMessage(error: unknown): string {
  if (!isHttpClientError(error)) {
    return "角色配置保存失败，请稍后重试。";
  }
  if (error.code === "AUTHZ_ROLE_MEMBER_INVALID") {
    return error.message || "检测到失效角色成员，请移除后重试。";
  }
  if (error.statusCode === 409) {
    return "角色配置已被其他管理员更新，请刷新后重新确认。";
  }
  switch (error.statusCode) {
    case 401:
      return "登录状态已失效，请重新登录。";
    case 403:
      return "当前账号没有保存角色配置的权限。";
    case 422:
      return "角色配置格式不正确，请检查后重试。";
    case 429:
      return "操作过于频繁，请稍后重试。";
    default:
      return error.statusCode !== null && error.statusCode >= 500
        ? "角色服务暂时不可用，请稍后重试。"
        : "角色配置保存失败，请稍后重试。";
  }
}

/** 根据节点勾选变化计算角色成员 ID。 */
export function resolveMemberSelection(
  tree: AuthzTreeNode[],
  selectedUserIds: number[],
  nodeKey: string,
  checked: boolean,
): number[] {
  const node = findTreeNode(tree, (item) => item.key === nodeKey);
  if (!node) {
    return [...new Set(selectedUserIds)].sort((left, right) => left - right);
  }
  const affected = collectUserIds(node, checked);
  const next = new Set(selectedUserIds);
  for (const userId of affected) {
    if (checked) {
      next.add(userId);
    } else {
      next.delete(userId);
    }
  }
  return [...next].sort((left, right) => left - right);
}

/** 根据严格勾选规则计算页面与操作权限。 */
export function resolvePermissionSelection(
  tree: AuthzTreeNode[],
  selectedPermissionCodes: string[],
  permissionCode: string,
  checked: boolean,
): string[] {
  const node = findTreeNode(tree, (item) => item.permissionCode === permissionCode);
  if (!node) {
    return [...new Set(selectedPermissionCodes)];
  }
  const next = new Set(selectedPermissionCodes);
  if (checked) {
    if (node.type === "action") {
      const parent = findTreeNode(tree, (item) => item.children.some((child) => child.key === node.key));
      if (parent?.permissionCode) {
        next.add(parent.permissionCode);
      }
    }
    next.add(permissionCode);
    return [...next];
  }
  next.delete(permissionCode);
  if (node.type === "page") {
    for (const childCode of collectPermissionCodes(node)) {
      next.delete(childCode);
    }
  }
  return [...next];
}

/** 按搜索词和仅看已选条件过滤树，同时保留完整祖先路径。 */
export function filterRoleTree(
  tree: AuthzTreeNode[],
  keyword: string,
  selectedOnly: boolean,
  selectedKeys: Set<string>,
): AuthzTreeNode[] {
  const normalizedKeyword = keyword.trim().toLocaleLowerCase();
  return tree.flatMap((node) => {
    const children = filterRoleTree(node.children, keyword, selectedOnly, selectedKeys);
    const searchable = `${node.title} ${node.permissionCode ?? ""}`.toLocaleLowerCase();
    const matchesKeyword = !normalizedKeyword || searchable.includes(normalizedKeyword);
    const stableKey = node.permissionCode ?? (node.userId ? String(node.userId) : node.key);
    const matchesSelected = !selectedOnly || selectedKeys.has(stableKey);
    if ((matchesKeyword && matchesSelected) || children.length > 0) {
      return [{ ...node, children }];
    }
    return [];
  });
}

/** 生成保存前的成员与权限差异摘要。 */
export function buildAuthorizationChangeSummary(input: AuthzChangeSummaryInput): string {
  const parts: string[] = [];
  if (input.addedUserIds.length) parts.push(`新增 ${input.addedUserIds.length} 名成员`);
  if (input.removedUserIds.length) parts.push(`移除 ${input.removedUserIds.length} 名成员`);
  if (input.addedPermissionCodes.length) parts.push(`新增 ${input.addedPermissionCodes.length} 项权限`);
  if (input.removedPermissionCodes.length) parts.push(`移除 ${input.removedPermissionCodes.length} 项权限`);
  return parts.join("，") || "未发生成员或权限变化";
}

/** 计算相对原配置的保存前摘要。 */
export function summarizeRoleDraft(
  originalUserIds: number[],
  originalPermissionCodes: string[],
  nextUserIds: number[],
  nextPermissionCodes: string[],
): string {
  return buildAuthorizationChangeSummary({
    addedUserIds: difference(nextUserIds, originalUserIds),
    removedUserIds: difference(originalUserIds, nextUserIds),
    addedPermissionCodes: difference(nextPermissionCodes, originalPermissionCodes),
    removedPermissionCodes: difference(originalPermissionCodes, nextPermissionCodes),
  });
}

/** 返回树中全部节点 key，供展开/收起控制。 */
export function collectTreeKeys(tree: AuthzTreeNode[]): string[] {
  return tree.flatMap((node) => [node.key, ...collectTreeKeys(node.children)]);
}

/** 判断新增权限中是否包含高风险操作。 */
export function hasNewHighRiskPermission(
  tree: AuthzTreeNode[],
  originalCodes: number[] | string[],
  nextCodes: string[],
): boolean {
  const original = new Set(originalCodes.map(String));
  return nextCodes.some((code) => {
    if (original.has(code)) return false;
    const node = findTreeNode(tree, (item) => item.permissionCode === code);
    return node?.riskLevel === "high";
  });
}

/** 收集节点及其子树中的有效员工 ID。 */
function collectUserIds(node: AuthzTreeNode, selecting: boolean): number[] {
  const own = node.type === "user" && node.userId && (!selecting || !node.disabled) ? [node.userId] : [];
  return [...own, ...node.children.flatMap((child) => collectUserIds(child, selecting))];
}

/** 收集节点及其子树中的权限码。 */
function collectPermissionCodes(node: AuthzTreeNode): string[] {
  return [node.permissionCode, ...node.children.flatMap(collectPermissionCodes)].filter(
    (value): value is string => Boolean(value),
  );
}

/** 按条件递归查找首个树节点。 */
function findTreeNode(
  tree: AuthzTreeNode[],
  predicate: (node: AuthzTreeNode) => boolean,
): AuthzTreeNode | undefined {
  for (const node of tree) {
    if (predicate(node)) return node;
    const child = findTreeNode(node.children, predicate);
    if (child) return child;
  }
  return undefined;
}

/** 返回左侧集合相对右侧集合的去重差集。 */
function difference<T>(left: T[], right: T[]): T[] {
  const rightSet = new Set(right);
  return [...new Set(left)].filter((item) => !rightSet.has(item));
}
